<div class="users-page">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <h1 class="page-title">Users</h1>
      <p class="page-subtitle">View and manage system users</p>
    </div>
    <div class="header-actions">
      <!-- Export Button -->
      <div class="export-dropdown-container">
        <ukg-button
          emphasis="high"
          size="medium"
          parent-icon="download"
          (click)="toggleExportMenu()"
          (keydown.enter)="toggleExportMenu()"
          (keydown.space)="toggleExportMenu(); $event.preventDefault()"
          aria-label="Export users data"
          [attr.aria-expanded]="exportMenuOpen"
          class="export-button">
          Export
        </ukg-button>
        @if (exportMenuOpen) {
          <div class="export-menu">
            <button
              class="export-menu-item"
              (click)="exportAsCSV()"
              aria-label="Export as CSV">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2">
                <path
                  d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                <polyline points="13 2 13 9 20 9"></polyline>
              </svg>
              CSV
            </button>
            <button
              class="export-menu-item"
              (click)="exportAsHTML()"
              aria-label="Export as HTML">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2">
                <path
                  d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                <polyline points="13 2 13 9 20 9"></polyline>
              </svg>
              HTML
            </button>
          </div>
        }
      </div>
      <!-- Search Input -->
      <app-search-input
        [value]="searchTerm"
        placeholder="Search users..."
        (valueChange)="onSearchChange($any($event))"
        (clear)="clearSearch()">
      </app-search-input>
    </div>
  </div>

  <!-- Initial Loading State -->
  @if ((loading$ | async) && !hasLoadedOnce) {
    <app-loading-state message="Loading users..."> </app-loading-state>
  }

  <!-- Error State -->
  @if (error$ | async; as error) {
    <app-error-state
      title="Unable to Load Users"
      [message]="error"
      (retry)="onRetry()">
    </app-error-state>
  }

  <!-- Data Grid -->
  @if (users$ | async; as pagedResult) {
    @if ((error$ | async) === null) {
      <!-- Custom Filter Panel -->
      <app-filter-panel-wrapper (clearFilters)="clearAllFilters()">
        <!-- Status Filter -->
        <div class="filter-field">
          <label for="statusFilterSelect">Status</label>
          <select
            id="statusFilterSelect"
            class="filter-select"
            [ngModel]="statusFilter === null ? '' : statusFilter.toString()"
            (change)="onStatusFilterChange($any($event.target).value)">
            <option value="">All Statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <!-- Role Filter -->
        <div class="filter-field">
          <label for="roleFilterSelect">Role</label>
          <select
            id="roleFilterSelect"
            class="filter-select"
            [ngModel]="roleFilter ?? ''"
            (change)="onRoleFilterChange($any($event.target).value)">
            <option value="">All Roles</option>
            @for (role of availableRoles; track role) {
              <option [value]="role">
                {{ role }}
              </option>
            }
          </select>
        </div>
      </app-filter-panel-wrapper>
      <!-- Grid with Data (or Empty Table) -->
      <div class="grid-container" [class.is-loading]="loading$ | async">
        <!-- Loading Overlay -->
        @if (loading$ | async) {
          <div class="grid-loading-overlay">
            <ukg-progress-circular></ukg-progress-circular>
          </div>
        }
        <!-- Active Filter Chips Bar -->
        <app-filter-chips-bar
          [chips]="activeColumnFilterChips"
          (clearChip)="clearColumnFilter($event)"
          (clearAll)="clearAllColumnFilters()">
        </app-filter-chips-bar>
        <!-- Results Summary -->
        <div class="grid-toolbar">
          <div class="results-info-row">
            @if (filteredTableData$ | async; as filteredData) {
              <app-results-info
                [pageNumber]="pagedResult.pageNumber"
                [pageSize]="pagedResult.pageSize"
                [totalCount]="pagedResult.totalCount"
                [filteredDataLength]="filteredData.length"
                itemLabel="users">
              </app-results-info>
            }
          </div>
        </div>
        <!-- Column Search Row -->
        <app-column-search-row
          [visibleColumns]="visibleColumns"
          [columnFilters]="columnFilters"
          [showColumnFilters]="true"
          [filterRowGridTemplate]="filterRowGridTemplate"
          [showCheckboxPlaceholder]="false"
          (columnSearch)="onColumnSearch($event.column, $event.value)"
          (clearFilter)="clearColumnFilter($event)">
        </app-column-search-row>
        <!-- Table with Headers Always Visible -->
        <div class="table-scroll-container">
          <ukg-data-table>
            <table>
              <thead>
                <tr>
                  @for (col of visibleColumns; track col) {
                    <th [style.width]="columnWidthMap[col.field]">
                      <button
                        class="sortable-header"
                        (click)="onSort(col.field)"
                        type="button">
                        {{ col.header }}
                        <span class="sort-icon">{{
                          getSortIcon(col.field)
                        }}</span>
                      </button>
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of filteredTableData$ | async; track row) {
                  <tr>
                    @for (col of visibleColumns; track col) {
                      <td [style.width]="columnWidthMap[col.field]">
                        {{ row.data[col.field] }}
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </ukg-data-table>
        </div>
        <!-- Pagination -->
        <app-pagination
          [pageNumber]="pagedResult.pageNumber"
          [pageSize]="pagedResult.pageSize"
          [totalPages]="pagedResult.totalPages"
          [hasPreviousPage]="pagedResult.hasPreviousPage"
          [hasNextPage]="pagedResult.hasNextPage"
          (pageChange)="onPageChange($any($event))"
          (pageSizeChange)="onPageSizeChange($any($event))">
          @if (filteredTableData$ | async; as filteredData) {
            @if (activeColumnFilterChips.length > 0) {
              Page 1 of {{ filteredData.length > 0 ? 1 : 0 }}
            }
            @if (activeColumnFilterChips.length === 0) {
              Page {{ pagedResult.pageNumber }} of {{ pagedResult.totalPages }}
            }
          }
        </app-pagination>
      </div>
    }
  }
</div>
